import React, {useState} from 'react';
import {FlatList, LayoutChangeEvent, Platform, Pressable, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react';

import {PROMOTIONS_CAROUSEL_NUM_ITEMS} from '../../constants/GlobalConstants';
import {resolveWeight, space, type} from '../../design-system/tokens';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {AirtablePromotionItem} from '../../models/airtable';
import {usePromotions} from '../../queries/promotions';
import {
  createFilterForStoreFulfilment,
  isCarouselPromotion,
} from '../../queries/promotions/predicates';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {brandRewardsCopy} from '../../utils/rewards';
import {visibleScrollbarProps} from '../../utils/webScrollbar';
import TitleLine from '../rewards/TitleLine';
import Text from '../themed/Text';
import TileLarge from '../TileLarge';

type PromotionsCarouselProps = {
  onPromotionPress?: (item: AirtablePromotionItem) => void;
  onSeeAllPress?: () => void;
  numItems?: number;
};

const PromotionsCarousel = observer(
  ({
    onPromotionPress,
    onSeeAllPress,
    numItems = PROMOTIONS_CAROUSEL_NUM_ITEMS,
  }: PromotionsCarouselProps) => {
    const {shopSelectionStore} = useStores();
    const {listedPromotions} = usePromotions();

    // DIS-1889: same list and order as Current Promotions, limited to carousel types.
    const canSellOnline = shopSelectionStore.canPickup || shopSelectionStore.canDeliver;
    const carouselPromotions = (listedPromotions ?? [])
      .filter(createFilterForStoreFulfilment(canSellOnline))
      .filter(isCarouselPromotion);
    const items = carouselPromotions.slice(0, numItems);
    const total = carouselPromotions.length;
    // DIS-1890: same trailing card as the Promotion Detail product listing. It opens
    // Current Promotions, so it is shown only when the caller gives it a destination.
    const showSeeAllCard = total > items.length && onSeeAllPress != null;

    // Measured height of the first tile, so the "See all" card matches the tiles.
    const [tileHeight, setTileHeight] = useState<number>();
    const handleTileLayout = (e: LayoutChangeEvent) => {
      setTileHeight(e.nativeEvent.layout.height);
    };

    const renderItem = createRenderItem(onPromotionPress, handleTileLayout);

    // A component rather than an element, so FlatList renders it like any other footer.
    const SeeAllCard = () => (
      <Pressable
        testID="promotions-carousel-see-all"
        onPress={onSeeAllPress}
        accessibilityRole="button"
        accessibilityLabel={`See all ${total} promotions`}
        style={[styles.seeAllCard, tileHeight != null && {height: tileHeight}]}>
        <Text style={styles.seeAllCount}>{`+${total - items.length}`}</Text>
        <Text style={styles.seeAllLabel}>{`SEE ALL ${total} PROMOTIONS`}</Text>
        <Text style={styles.seeAllChevron}>{'›'}</Text>
      </Pressable>
    );

    const {isWebDesktopScreen} = useWebScreenDimensions();

    if (!items.length) {
      return null;
    }

    return (
      <View>
        <TitleLine
          title={brandRewardsCopy.promotionsTitle.toUpperCase()}
          onSeeAllPress={onSeeAllPress}
        />
        <FlatList
          horizontal
          data={items}
          renderItem={renderItem}
          ItemSeparatorComponent={Separator}
          showsHorizontalScrollIndicator={isWebDesktopScreen}
          {...visibleScrollbarProps(isWebDesktopScreen)}
          windowSize={3}
          maxToRenderPerBatch={3}
          initialNumToRender={3}
          removeClippedSubviews={Platform.OS === 'android'}
          ListFooterComponent={showSeeAllCard ? SeeAllCard : null}
        />
      </View>
    );
  },
);

function Separator() {
  return <View style={{width: 8, height: 8}} />;
}

const createRenderItem =
  (
    onPromotionPress?: (item: AirtablePromotionItem) => void,
    onFirstTileLayout?: (e: LayoutChangeEvent) => void,
  ) =>
  ({item, index}: {item: AirtablePromotionItem; index: number}) => {
    return (
      <View onLayout={index === 0 ? onFirstTileLayout : undefined}>
        <TileLarge
          title={item.headline}
          description={item.description}
          image={item.heroImage!}
          onPress={() => onPromotionPress?.(item)}
        />
      </View>
    );
  };

// Same CTA colours and layout as the "See all" card in PromotionProductListing.
const CTA_BG = theme.colour.background.button.primary;
const CTA_ON = theme.colour.background.button.contrastOnPrimary;

// SHORTCUT: seeAllCard.borderRadius 10, seeAllCount.fontSize 32 and seeAllChevron.fontSize 22
// match the PromotionProductListing "See all" card, and no radius or type token holds them.
// Ceiling: the two cards drift if one changes. Upgrade path: one shared SeeAllCard component
// once the design system defines a card-radius and display-number token.
const styles = StyleSheet.create({
  seeAllCard: {
    width: 160,
    marginLeft: space.s8,
    alignItems: 'center',
    justifyContent: 'center',
    padding: space.s16,
    borderRadius: 10,
    backgroundColor: CTA_BG,
  },
  seeAllCount: {
    color: CTA_ON,
    fontSize: 32,
    ...resolveWeight('bold'),
  },
  seeAllLabel: {
    ...type.paragraph.p3,
    ...resolveWeight('bold'),
    marginTop: space.s8,
    color: CTA_ON,
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  // SHORTCUT: fontSize 22 matches the PromotionProductListing chevron; see the note above.
  seeAllChevron: {
    marginTop: space.s2,
    color: CTA_ON,
    fontSize: 22,
  },
});

export default PromotionsCarousel;
